import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import type { Dispatch } from 'redux';

import { ListTypeEnum } from 'APIPackage/CatalogsModule/enums/ListTypeEnum';
import { KnmTypeIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeIdsByCodeEnum';
import {
  getIsPlanErknmStatusApproved,
  getIsPlanErknmStatusInProcess,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import {
  getUserAccountTypeIsInternal,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IListTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IListTypeSelectOption';
import {
  getCatalogKnmTypes248SelectOptionsSelector,
  getCatalogListTypesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ROUTE_KNM_LIST_BY_ID_AND_TYPE } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmListByPlanFiltersModal } from 'KnmListByPlanPackage/KnmListByPlanFiltersModule/components/KnmListByPlanFiltersModal';
import { filterListTypesByPlanStatus } from 'PlanPackage/MainModule/common/filterListTypesByPlanStatus';
import {
  getPlanData,
  getPlanDataErknmStatus,
  getPlanWatchOnlyByTestUser,
} from 'PlanPackage/MainModule/selectors/planSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { knmGetSuccess } from 'KnmPackage/MainModule/actions/knmActions';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { PlanAddModal } from 'PlanPackage/MainModule/components/PlanAddModal';
import { IKnmReasonChangeType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonChangeType';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { UploadModal, IUploadModalForm } from 'ImportPackage/MainModule/components/UploadModal';
import { importFileTypesProcErknm } from 'ImportPackage/MainModule/constants/importFileTypesProcErknm';
import { importFileTypesProc } from 'ImportPackage/MainModule/constants/importFileTypesProc';
import { importFileTypesExecErknm } from 'ImportPackage/MainModule/constants/importFileTypesExecErknm';
import { importFileTypesExec } from 'ImportPackage/MainModule/constants/importFileTypesExec';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { importCreateTemplateWithFile } from 'ImportPackage/MainModule/actions/importActions';
import { getAccountOrganizationsWithDistrictData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { KnmRemoveFromPlanModal } from 'CorePackage/MainModule/components/KnmRemoveFromPlanModal';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { importFileTypesTestExecErknm } from 'ImportPackage/MainModule/constants/importFileTypesTestExecErknm';
import { createNewKnmFromPlan } from 'PlanPackage/MainModule/common/createNewKnmFromPlan';

import {
  knmListByPlanSetSearchValue,
  knmListByPlanGetData,
  knmListByPlanFilterSetSwitcher,
  knmListByPlanExcludeAction,
} from '../actions/knmListByPlanActions';
import {
  getKnmListByPlanTypeList,
  getKnmListByPlanSearchValue,
  getKnmListByPlanData,
  getKnmListByPlanTotalCount,
  getKnmListByPlanIsFilterOn,
} from '../selectors/knmListByPlanSelectors';
import { KnmListByPlanCommentModal } from './KnmListByPlanCommentModal';
import styles from './KnmListByPlanPanel.module.scss';
import { KnmListByPlanDropdown } from './KnmListByPlanDropdown';
import { KnmListByPlanApproveRemarksModal } from './KnmListByPlanApproveRemarksModal';

const mapListTypesDescription: Record<ListTypeEnum, string> = {
  [ListTypeEnum.TOTAL]: 'Все',
  [ListTypeEnum.UNDER_CONSIDERATION]: 'На рассмотрении',
  [ListTypeEnum.AGREED]: 'Согласованные',
  [ListTypeEnum.EXCLUDED]: 'Исключенные',
  [ListTypeEnum.APPEALED]: 'С обжалованным решением',
  [ListTypeEnum.COMMENTS_EXIST]: 'С замечаниями',
  [ListTypeEnum.FILL_IN_PROCESS]: 'В процессе заполнения',
  [ListTypeEnum.READY_FOR_APPROVE]: 'Готовые к согласованию',
};

const SEARCH_INPUT_ERROR = 'Длина строки должна быть не менее 3 символов';

interface IKnmListByPlanPanelProps {
  itemsCheck: TableCheckItems;
  planIdNumber: number;
  handleDropdownDeleteButtonClick: () => void;
}

interface IKnmListByPlanPanelStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  plan: IKnmPlan | undefined;
  planErknmStatus: ErknmPlanStatusesEnum | undefined;
  typeList: ListTypeEnum | undefined;
  searchValue: string | undefined;
  listTypesSelectOptions: IListTypeSelectOption[];
  knms: IKnmRaw[];
  knmsTotalCount: number;
  isFilterOn: boolean;
  isErknm: boolean;
  knmTypes: IKnmTypeSelectOption[];
  userProsecutorOrganisations: IUserOrganisation[];
  isInternalAccount: boolean;
  isWatchOnlyByTestUser: boolean;
}

const KnmListByPlanPanelWithoutStore = ({
  itemsCheck,
  planIdNumber,
  handleDropdownDeleteButtonClick,
  isErknm,
  isFilterOn,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  knms,
  knmsTotalCount,
  listTypesSelectOptions,
  plan,
  planErknmStatus,
  searchValue,
  typeList,
  knmTypes,
  userProsecutorOrganisations,
  isInternalAccount,
  isWatchOnlyByTestUser,
}: IKnmListByPlanPanelProps & IKnmListByPlanPanelStoreProps) => {
  const isPlanErknmStatusInProcess: boolean = getIsPlanErknmStatusInProcess(planErknmStatus);
  const isPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(planErknmStatus);

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const dispatchKnmListByPlanGetData: () => void = useCallback(() => dispatch(knmListByPlanGetData()), [dispatch]);
  const setInitialKnmData: (initialData: IKnm | undefined) => void = useCallback(
    (initialData: IKnm | undefined) => initialData && dispatch(knmGetSuccess({ ...initialData, initial: true })),
    [dispatch],
  );

  const history = useHistory();

  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [isExcludeCommentVisible, setIsExcludeCommentVisible] = useState(false);
  const [isRemoveFromPlanModalVisible, setIsRemoveFromPlanModalVisible] = useState(false);
  const [isAddKnmModalVisible, setIsAddKnmModalVisible] = useState(false);
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [isApproveRemarksModalVisible, setIsApproveRemarksModalVisible] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');
  const selectedKnmsIds = Object.keys(itemsCheck).filter(key => itemsCheck[key]);
  const selectedKnmsNumber = selectedKnmsIds.length;

  const userProsecutorOrganisationsSelectOptions:
    | IUserOrganisationSelectOption[]
    | undefined = userProsecutorOrganisations?.map(item => ({
    ...item,
    label: `${item.districtName ? `${item.districtName}, ` : ''}${item.fullName}`,
    value: item.id,
    districtName: item.districtName,
    districtId: item.districtId,
  }));

  const customListTypesSelectOptions: IListTypeSelectOption[] = useMemo(() => {
    const filteredListTypesSelectOptions = filterListTypesByPlanStatus(listTypesSelectOptions, planErknmStatus);

    return filteredListTypesSelectOptions.map(item => ({
      ...item,
      label: mapListTypesDescription[item.type],
    }));
  }, [listTypesSelectOptions, planErknmStatus]);

  const statusFieldValue: IListTypeSelectOption | undefined = useMemo(() => {
    const activeListTypeSelectOption = customListTypesSelectOptions.find(item => item.type === typeList);

    if (!activeListTypeSelectOption) {
      return;
    }

    return {
      ...activeListTypeSelectOption,
      label: `${activeListTypeSelectOption?.label} (${knmsTotalCount})`,
    };
  }, [typeList, customListTypesSelectOptions, knmsTotalCount]);

  const getFileTypes = () => {
    if (isProsecutorOrSuperProsecutor) {
      return isErknm ? importFileTypesProcErknm : importFileTypesProc;
    }
    if (isSupervisor) {
      return isErknm
        ? (isInternalAccount && importFileTypesTestExecErknm) || importFileTypesExecErknm
        : importFileTypesExec;
    }

    return [];
  };

  const handleUploadModalApply = useCallback(
    (values: IUploadModalForm) => {
      dispatch(importCreateTemplateWithFile(values, values.planId || undefined));
    },
    [dispatch],
  );

  const closeUploadModal = useCallback(() => setIsUploadModalVisible(false), []);
  const handleUploadButtonClick = useCallback(() => {
    setIsUploadModalVisible(true);
  }, []);

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError(SEARCH_INPUT_ERROR);
    } else {
      setSearchInputError('');
      dispatchKnmListByPlanGetData();
    }
  }, [dispatchKnmListByPlanGetData, isSearchInputValid]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError(SEARCH_INPUT_ERROR) : setSearchInputError('');
  }, [isSearchInputValid]);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      dispatch(knmListByPlanSetSearchValue(String(value)));
      const isValid = value.length > 2 || value.length === 0;

      setIsSearchInputValid(isValid);
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => setIsFiltersModalVisible(true), []);
  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  const handleDropdownExcludeButtonClick = useCallback(() => {
    if (isSupervisor) {
      setIsRemoveFromPlanModalVisible(true);
    } else {
      setIsExcludeCommentVisible(true);
    }
  }, [isSupervisor]);

  const handleRemoveFromPlanModalClose = useCallback(() => setIsRemoveFromPlanModalVisible(false), []);

  const handleCommentModalClose = useCallback(() => {
    setIsCommentModalVisible(false);
    setIsExcludeCommentVisible(false);
  }, []);

  const handleApproveRemarksModalOpen = useCallback(() => {
    setIsApproveRemarksModalVisible(true);
  }, []);

  const handleApproveRemarksModalClose = useCallback(() => {
    setIsApproveRemarksModalVisible(false);
  }, []);

  const createNewKnm = useCallback(
    (selectedReason?: IKnmReasonChangeType, documents?: IKnmDocumentRequest) => {
      createNewKnmFromPlan({
        history,
        knmTypes,
        plan,
        setInitialKnmData,
        userProsecutorOrganisationsSelectOptions,
        documents,
        selectedReason,
      });
    },
    [history, knmTypes, plan, setInitialKnmData, userProsecutorOrganisationsSelectOptions],
  );

  const handleAddKnmButtonClick = useCallback(() => {
    const currentDate = new Date();

    const currentYear = currentDate.getFullYear();
    const nextYear = currentDate.getFullYear() + 1;

    /**
     * В результате обсуждений TASK-5292 аналитиками установлено,
     * что указывать changePlanReason необходимо для Утвержденного плана
     * текущего и СЛЕДУЮЩЕГО года.
     */
    if (isPlanErknmStatusApproved && (plan?.year === currentYear || plan?.year === nextYear)) {
      setIsAddKnmModalVisible(true);
    } else {
      createNewKnm();
    }
  }, [createNewKnm, isPlanErknmStatusApproved]);

  const isSomeSelectedKnmDeleted = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.deleted),
  );

  const isSomeSelectedKnmCreatedByErpDb = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.createdByErpDb),
  );

  const isSomeSelectedKnmTypePlan = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.knmTypeId === KnmTypeIdsByCodeEnum.PP),
  );

  const isDropdownDeleteItemsDisabled =
    selectedKnmsNumber === 0 ||
    (selectedKnmsNumber > 0 && isSomeSelectedKnmDeleted) ||
    isSomeSelectedKnmCreatedByErpDb ||
    (isSupervisor && isSomeSelectedKnmTypePlan);

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(knmListByPlanFilterSetSwitcher(e.target.checked));
      dispatchKnmListByPlanGetData();
    },
    [dispatch, dispatchKnmListByPlanGetData],
  );

  const handleAddKnmModalClose = useCallback(() => setIsAddKnmModalVisible(false), []);

  const handleStatusFieldChange = useCallback(
    (value: IListTypeSelectOption) => history.push(ROUTE_KNM_LIST_BY_ID_AND_TYPE(planIdNumber, value.type)),
    [history, planIdNumber],
  );

  const handleApplyModalToAddKnm = useCallback(
    (selectedReason: IKnmReasonChangeType, documents: IKnmDocumentRequest) => createNewKnm(selectedReason, documents),
    [createNewKnm],
  );

  const handleRemoveFromPlanModalApply = useCallback(
    (params?: IKnmMoveToPlanAttachment) => dispatch(knmListByPlanExcludeAction({ ids: selectedKnmsIds, params })),
    [dispatch, selectedKnmsIds],
  );

  const filterButtonAttributes = {
    ...(isFilterOn && { 'data-active': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Container}>
        {!isWatchOnlyByTestUser && isSupervisor && (isPlanErknmStatusInProcess || isPlanErknmStatusApproved) && (
          <Button
            startIcon={styles.AddIcon}
            className={styles.Button}
            text="Добавить"
            color="primary"
            onClick={handleAddKnmButtonClick}
          />
        )}
        {!isWatchOnlyByTestUser && isPlanErknmStatusInProcess && !isProsecutorOrSuperProsecutor && (
          <Button
            className={styles.ButtonDelete}
            startIcon={styles.DeleteIcon}
            text="Удалить"
            id="deleteButton"
            disabled={isDropdownDeleteItemsDisabled}
            onClick={handleDropdownDeleteButtonClick}
          />
        )}
        <SearchInput
          className={styles.SearchInput}
          name="searchString"
          placeholder="Поиск КНМ"
          value={searchValue}
          buttonDisabled={!isSearchInputValid}
          error={searchInputError}
          onChange={handleSearchInputChange}
          onSubmit={handleSearchInputSubmit}
          onBlur={handleOnBlur}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск плана КНМ по следующим критериям:
              <ul>
                <li>Номер КНМ</li>
                <li>ИНН</li>
                <li>ОГРН</li>
                <li>Наименование проверяемого лица</li>
              </ul>
              Расширенный поиск плана КНМ осуществляется с помощью Фильтра.
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Container}>
        <div className={styles.Status}>
          <div className={styles.StatusLabel}>Статус</div>
          <SelectInput
            className={styles.StatusField}
            id="status"
            name="status"
            isClearable={false}
            options={customListTypesSelectOptions}
            value={statusFieldValue}
            onChange={handleStatusFieldChange}
          />
        </div>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="filterToggler"
          name="filterToggler"
          checked={isFilterOn}
          onChange={handleFilterCheckboxChange}
        />
        <Button
          className={styles.FilterButton}
          text="Фильтр..."
          color="info"
          onClick={handleFilterButtonClick}
          {...filterButtonAttributes}
        />
        <KnmListByPlanDropdown
          itemsCheck={itemsCheck}
          isDropdownDeleteItemsDisabled={isDropdownDeleteItemsDisabled}
          handleAddKnmButtonClick={handleAddKnmButtonClick}
          handleUploadButtonClick={handleUploadButtonClick}
          handleDropdownDeleteButtonClick={handleDropdownDeleteButtonClick}
          handleDropdownExcludeButtonClick={handleDropdownExcludeButtonClick}
          handleApproveRemarksModalOpen={handleApproveRemarksModalOpen}
          selectedKnmsIds={selectedKnmsIds}
          selectedKnmsNumber={selectedKnmsNumber}
        />
      </div>
      {isFiltersModalVisible && (
        <KnmListByPlanFiltersModal visible={isFiltersModalVisible} onClose={closeFiltersModal} />
      )}
      <KnmListByPlanCommentModal
        visible={isCommentModalVisible}
        isExcludeComment={isExcludeCommentVisible}
        onClose={handleCommentModalClose}
        itemsCheck={itemsCheck}
      />
      <KnmRemoveFromPlanModal
        visible={isRemoveFromPlanModalVisible}
        knmsNumber={selectedKnmsNumber}
        isErknm={isErknm}
        onApply={handleRemoveFromPlanModalApply}
        onClose={handleRemoveFromPlanModalClose}
      />
      <PlanAddModal
        visible={isAddKnmModalVisible}
        isErknm={isErknm}
        onClose={handleAddKnmModalClose}
        planId={planIdNumber}
        onApply={handleApplyModalToAddKnm}
        isKnm
      />
      <UploadModal
        visible={isUploadModalVisible}
        fileTypes={getFileTypes()}
        onApply={handleUploadModalApply}
        onClose={closeUploadModal}
        isAdjustment
      />
      <KnmListByPlanApproveRemarksModal
        visible={isApproveRemarksModalVisible}
        onClose={handleApproveRemarksModalClose}
        itemsCheck={itemsCheck}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmListByPlanPanelStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  plan: getPlanData(state),
  planErknmStatus: getPlanDataErknmStatus(state),
  typeList: getKnmListByPlanTypeList(state),
  searchValue: getKnmListByPlanSearchValue(state),
  listTypesSelectOptions: getCatalogListTypesSelectOptions(state),
  knms: getKnmListByPlanData(state),
  knmsTotalCount: getKnmListByPlanTotalCount(state),
  isFilterOn: getKnmListByPlanIsFilterOn(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  knmTypes: getCatalogKnmTypes248SelectOptionsSelector(state)({}),
  userProsecutorOrganisations: getAccountOrganizationsWithDistrictData(state).prosecutor,
  isInternalAccount: getUserAccountTypeIsInternal(state),
  isWatchOnlyByTestUser: getPlanWatchOnlyByTestUser(state),
});

export const KnmListByPlanPanel = connect(mapStateToProps)(KnmListByPlanPanelWithoutStore);
